import { CSSProperties, ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export interface DraggableRowProps {
  /** Unique ID for the row - used by dnd-kit to identify the sortable item */
  rowId: string;
  /** Row content */
  children: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Additional inline styles (base positioning via 'top', not 'transform') */
  style?: CSSProperties;
  /** Data index for virtualization measurement */
  "data-index"?: number;
  /** Click handler */
  onClick?: () => void;
  /** Context menu handler */
  onContextMenu?: (e: React.MouseEvent) => void;
  /** Mouse enter handler */
  onMouseEnter?: () => void;
  /** Mouse leave handler */
  onMouseLeave?: () => void;
}

/**
 * Wrapper component for draggable table rows using dnd-kit.
 * Must be used inside a SortableContext.
 *
 * IMPORTANT: For virtualized tables, base positioning should use CSS 'top' property,
 * NOT 'transform'. This allows dnd-kit to fully control the transform property for
 * drag animations and the verticalListSortingStrategy to work correctly.
 */
export function DraggableRow({
  rowId,
  children,
  className = "",
  style: propStyle = {},
  "data-index": dataIndex,
  onClick,
  onContextMenu,
  onMouseEnter,
  onMouseLeave,
}: DraggableRowProps) {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: rowId,
  });

  // Let dnd-kit fully control the transform property (like TanStack example)
  // Base positioning comes from propStyle.top, not transform
  const style: CSSProperties = {
    ...propStyle,
    transform: CSS.Transform.toString(transform), // Only dnd-kit's transform
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  // Debug logging
  if (transform) {
    console.log(`[DraggableRow ${rowId}] transform:`, transform, "top:", propStyle.top, "isDragging:", isDragging);
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={className}
      data-index={dataIndex}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>
  );
}
